import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X as XIcon, Instagram, Youtube, MessageCircle, Heart } from "lucide-react";

const SESSION_KEY = "ep_social_popup_shown";
const DISMISSED_AT_KEY = "ep_social_popup_dismissed_at"; // localStorage timestamp (ms)

// Inline TikTok glyph (lucide-react does not ship one)
function TikTokIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.6 6.3c-1.7-.4-3-1.5-3.9-3-.2-.4-.4-.8-.5-1.3h-3v13.6c0 1.6-1.3 2.9-2.9 2.9-1.6 0-2.9-1.3-2.9-2.9 0-1.6 1.3-2.9 2.9-2.9.3 0 .6 0 .9.1V9.7c-.3 0-.6-.1-.9-.1-3.3 0-6 2.7-6 6s2.7 6 6 6 6-2.7 6-6V8.7c1.3.8 2.9 1.3 4.5 1.3V6.8c-.1 0-.2 0-.2-.1-.1-.1-.1-.2 0-.4z"/>
    </svg>
  );
}

/**
 * SocialPopup — smart visitor engagement.
 * Triggers when EITHER:
 *   (a) the visitor has scrolled past N distinct sections, OR
 *   (b) the visitor has been on the site for M seconds.
 *
 * Once dismissed, stays dismissed for `popup_cooldown_days` (admin-configurable, default 7).
 * Set cooldown to 0 to use session-only (legacy behavior).
 */
export default function SocialPopup({ settings }) {
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const seenSectionsRef = useRef(new Set());

  const isAdmin = location.pathname.startsWith("/admin");
  const enabled = !isAdmin && settings && settings.popup_enabled !== false;
  const triggerSeconds = Math.max(5, settings?.popup_trigger_seconds ?? 60);
  const triggerSections = Math.max(1, settings?.popup_trigger_sections ?? 3);
  const cooldownDays = Math.max(0, settings?.popup_cooldown_days ?? 7);
  const cooldownMs = cooldownDays * 24 * 60 * 60 * 1000;

  // Check if visitor is still inside the dismissal cooldown window
  const isInCooldown = () => {
    if (cooldownDays === 0) return false; // session-only mode: cooldown handled by sessionStorage below
    try {
      const ts = parseInt(localStorage.getItem(DISMISSED_AT_KEY) || "0", 10);
      if (!ts) return false;
      return (Date.now() - ts) < cooldownMs;
    } catch { return false; }
  };

  useEffect(() => {
    if (!enabled) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    if (isInCooldown()) return;

    let timerId, intersectionObs, mutationObs;
    const trigger = () => {
      if (sessionStorage.getItem(SESSION_KEY) || isInCooldown()) return;
      sessionStorage.setItem(SESSION_KEY, "1");
      setShow(true);
      requestAnimationFrame(() => setAnimateIn(true));
    };

    timerId = setTimeout(trigger, triggerSeconds * 1000);

    const observeSections = () => {
      const els = Array.from(document.querySelectorAll("section, [data-track-section]"));
      if (intersectionObs) intersectionObs.disconnect();
      intersectionObs = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            const id = e.target.getAttribute("data-testid") || e.target.id || e.target.dataset.trackSection;
            const key = id || `section-${Array.from(document.querySelectorAll("section")).indexOf(e.target)}`;
            seenSectionsRef.current.add(key);
            if (seenSectionsRef.current.size >= triggerSections) {
              trigger();
              intersectionObs.disconnect();
              return;
            }
          }
        }
      }, { threshold: 0.25, rootMargin: "0px 0px -10% 0px" });
      els.forEach((el) => intersectionObs.observe(el));
    };
    observeSections();
    mutationObs = new MutationObserver(observeSections);
    mutationObs.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(timerId);
      intersectionObs?.disconnect();
      mutationObs?.disconnect();
    };
  }, [enabled, triggerSeconds, triggerSections, cooldownDays]); // eslint-disable-line

  useEffect(() => {
    seenSectionsRef.current = new Set();
  }, [location.pathname]);

  const close = () => {
    try { localStorage.setItem(DISMISSED_AT_KEY, String(Date.now())); } catch {/* ignore */}
    setAnimateIn(false);
    setTimeout(() => setShow(false), 350);
  };

  if (!enabled || !show) return null;

  const instaUrl = settings?.instagram || "https://instagram.com/elitepixels_";
  const ytUrl = settings?.youtube || "https://youtube.com/@elitepixels330";
  const tiktokUrl = settings?.tiktok || "";
  const phone = (settings?.whatsapp || "").replace(/\D/g, "");
  const whatsappUrl = settings?.whatsapp_channel_url || (phone ? `https://wa.me/${phone}` : "");

  // Build the list of buttons present
  const buttons = [
    instaUrl && { key: "instagram", url: instaUrl, label: settings?.popup_instagram_label || "Follow on Instagram",
      icon: <Instagram size={18} />, style: "bg-gradient-to-r from-[#E1306C] via-[#C13584] to-[#833AB4] text-white hover:opacity-95 hover:shadow-[0_6px_20px_-8px_rgba(225,48,108,0.7)]",
      testid: "social-popup-instagram" },
    ytUrl && { key: "youtube", url: ytUrl, label: settings?.popup_youtube_label || "Subscribe on YouTube",
      icon: <Youtube size={18} />, style: "bg-[#FF0000] text-white hover:bg-[#cc0000] hover:shadow-[0_6px_20px_-8px_rgba(255,0,0,0.7)]",
      testid: "social-popup-youtube" },
    tiktokUrl && { key: "tiktok", url: tiktokUrl, label: settings?.popup_tiktok_label || "Follow on TikTok",
      icon: <TikTokIcon size={18} />, style: "bg-black text-white border border-white/15 hover:bg-[#111] hover:border-[#D4AF37]/50 relative",
      testid: "social-popup-tiktok" },
    whatsappUrl && { key: "whatsapp", url: whatsappUrl, label: settings?.popup_whatsapp_label || "Join WhatsApp Channel",
      icon: <MessageCircle size={18} />, style: "bg-[#25D366] text-white hover:bg-[#1ebe5b] hover:shadow-[0_6px_20px_-8px_rgba(37,211,102,0.7)]",
      testid: "social-popup-whatsapp" },
  ].filter(Boolean);

  return (
    <div
      data-testid="social-popup"
      className={`fixed inset-0 z-[200] flex items-center justify-center p-4 transition-all duration-500 ${animateIn ? "opacity-100" : "opacity-0"}`}
      style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(14px)" }}
      onClick={close}
      role="dialog" aria-modal="true" aria-labelledby="social-popup-title"
    >
      <div onClick={(e) => e.stopPropagation()}
        className={`relative max-w-md w-full transition-all duration-700 ease-out ${animateIn ? "translate-y-0 scale-100" : "translate-y-6 scale-95"}`}>
        <div className="relative bg-gradient-to-br from-[#0a0a0a]/95 to-[#1a1612]/95 border border-[#D4AF37]/30 p-7 md:p-9 shadow-[0_25px_80px_-20px_rgba(212,175,55,0.25)]"
             style={{ backdropFilter: "blur(20px)" }}>
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
          <div className="absolute top-2 left-2 w-3 h-3 border-t border-l border-[#D4AF37]/60" />
          <div className="absolute top-2 right-2 w-3 h-3 border-t border-r border-[#D4AF37]/60" />
          <div className="absolute bottom-2 left-2 w-3 h-3 border-b border-l border-[#D4AF37]/60" />
          <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-[#D4AF37]/60" />

          <button onClick={close} data-testid="social-popup-close" aria-label="Close popup"
            className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center text-white/50 hover:text-[#D4AF37] hover:bg-white/5 transition">
            <XIcon size={18} />
          </button>

          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center">
              <Heart size={20} className="text-[#D4AF37] fill-[#D4AF37]/30" />
            </div>
          </div>

          <div className="text-center">
            <div className="eyebrow mb-2">Stay Close</div>
            <h3 id="social-popup-title" className="font-serif font-light text-2xl md:text-3xl tracking-tight leading-tight">
              {settings?.popup_headline || "Stay Connected With Elite Pixels"}
            </h3>
            <p className="text-white/70 text-sm leading-relaxed mt-3">
              {settings?.popup_message || "Follow us for wedding inspiration, behind-the-scenes moments, highlight films, and exclusive content."}
            </p>
          </div>

          {/* Buttons — wraps onto two columns when 3+ visible */}
          <div className={`mt-6 ${buttons.length >= 3 ? "grid grid-cols-2 gap-2.5" : "space-y-2.5"}`}>
            {buttons.map(b => (
              <a key={b.key} href={b.url} target="_blank" rel="noreferrer"
                 data-testid={b.testid} onClick={close}
                 className={`flex items-center justify-center gap-2 px-4 py-3 font-medium tracking-wide text-sm transition-all ${b.style}`}>
                {b.icon}
                <span className="whitespace-nowrap">{b.label}</span>
              </a>
            ))}
          </div>

          <button onClick={close} data-testid="social-popup-skip"
            className="block mx-auto mt-5 text-[0.65rem] tracking-[0.25em] uppercase text-white/35 hover:text-white/70 transition">
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
